import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import {
  getCustomerLoyaltyOffers,
  logClientError,
} from "../../utils/APICalls/APICalls";
import { getCookie } from "../../utils/CookieHelper";
import {
  COOKIE_ENUM,
  LocalStorageEnum,
  OrderTypesEnum,
} from "../../utils/enums";
import env from "../../utils/env.config";
import { AlertErrorType } from "../../utils/ErrorTypes";
import { getAllDisabledItems } from "../../utils/help/itemDetails/itemDetails";
import * as userHelper from "../../utils/help/user/user";
import LS from "../../utils/LocalStorage";
import {
  getLoyaltyChannel,
  mapToLoyaltyOrderType,
} from "../../utils/LoyaltyUtils/loyaltyMapper";
import Offer from "../../utils/OfferClass/Offer/Offer";
import { createOffer } from "../../utils/OfferClass/OfferClassUtils/OfferCreator/offerCreator";
import { offersCache } from "../../utils/simpleCache";
import { LoyaltyOfferType } from "../../utils/loyaltyTypes";
import { OfferType } from "../../utils/types";
import { fetchItemStatusRedux } from "../redux/http/itemStatusFetch";
import { selectDeliveryType } from "../redux/selectors/deliverySelectors";
import { selectIsVerifiedOrganizationCustomer } from "../redux/selectors/loyaltyServiceSelectors";
import { linkUserOffersRedux } from "../redux/slices/cartAndLoyaltySlice";
import {
  removeOfferAction,
  setCustomerLoyaltyOffers,
} from "../redux/slices/cartLoyaltyV2Slice";
import { setOrderDataLoading } from "../redux/slices/globalComponentsToggleSlice";
import { sendUserOfferRequest } from "../redux/slices/requestLimiterSlice";
import { useAppDispatch, useAppSelector } from "./useReduxHooks";

type OffersResult = {
  data: {
    offers: OfferType[];
    isFirstTimeOrder: boolean;
    isTwistOfferAvailable: boolean;
  };
};

const noOffers = (): OffersResult => ({
  data: { offers: [], isFirstTimeOrder: false, isTwistOfferAvailable: false },
});

const lodashClonedeep = require("lodash.clonedeep");

type UserOffersResult = {
  data: {
    offers: OfferType[];
    isFirstTimeOrder: boolean;
    isTwistOfferAvailable: boolean;
  };
  loyaltyOffers?: LoyaltyOfferType[];
};

const useLinkOffers = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const userOfferRequests = useAppSelector(
    (state) => state.requestLimiter.userOfferRequests
  );
  const selectedFranchise = useAppSelector(
    (state) => state.config.selectedFranchise
  );

  const cartAndLoyalty = useAppSelector((state) => state.cartAndLoyaltyReducer);

  const cartLoyaltyV2 = useAppSelector((state) => state.cartLoyaltyV2Reducer);
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);
  const orderTypeParamState = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const token = useAppSelector((state) => state.userInfo.token);
  const { order_type } = router.query;

  const isOrganizationCustomer = useAppSelector(
    selectIsVerifiedOrganizationCustomer
  );

  const offlineOfferData = useAppSelector((state) => state.userInfo.offerData);
  const customerShortId = useAppSelector(
    (state) => state.userInfo.customerShortId
  );
  const [alertSent, setAlertSent] = useState(false);

  const bottomSheetModal = useAppSelector(
    (state) => state.globalComponentsToggleSlice.bottomSheetModal
  );
  const franchiseDeliveryTypeDisplayName = useAppSelector(
    (state) => selectDeliveryType(state)?.displayName ?? null
  );

  const linkUserOffer = useCallback(
    (
      offersPerOrderTypes: OfferType[],
      isFirstTimeOrder: boolean,
      isTwistOfferAvailable: boolean,
      order_type?: string
    ) => {
      let offersArray: OfferType[] = lodashClonedeep(offersPerOrderTypes);

      offersArray = offersArray.map((offer) => {
        return { ...offer, isCartV2: true };
      });

      let redeemedOffer: Offer | undefined;
      if (cartLoyaltyV2.cart.redeemedOffer) {
        const freshOffersArray = offersArray.map((o) => createOffer(o));
        redeemedOffer = freshOffersArray.find((o) =>
          o.isEqual(cartLoyaltyV2.cart.redeemedOffer)
        );

        if (!redeemedOffer) {
          dispatch(removeOfferAction());
        }
      }
      dispatch(
        linkUserOffersRedux({
          offersArray,
          orderType: (order_type as OrderTypesEnum) || orderTypeParamState,
          isFirstTimeOrder,
          isTwistOfferAvailable,
          redeemedOffer,
        })
      );
    },
    [
      dispatch,
      orderTypeParamState,
      itemStatusRedux,
      selectedFranchise?.id,
      cartAndLoyalty.offersArray.length,
    ]
  );

  useEffect(() => {
    const allDisabledItemsIds = getAllDisabledItems(
      itemStatusRedux,
      orderTypeParam
    );
    const restaurantId = restaurantInfo?.id || parseInt(env.restaurantId!);

    if (restaurantInfo?.timeZone && getCookie(COOKIE_ENUM.TOKEN)) {
      let fetchUserOffers: UserOffersResult | Promise<UserOffersResult>;
      let cacheKey: string | null = null;

      if (LS._get(LocalStorageEnum.DEVICE_NUMBER) && userHelper.isVisitor()) {
        fetchUserOffers = {
          data: {
            offers: [],
            isFirstTimeOrder: false,
            isTwistOfferAvailable: false,
          },
        };
      } else {
        // Generate cache key for offers request
        const decodedToken = token ? userHelper.parseJwt(token) : undefined;
        const tokenCustomerId =
          decodedToken?.customer_id ?? customerShortId ?? "none";
        cacheKey = `offers:${restaurantId}:${
          selectedFranchise?.slug
        }:${tokenCustomerId}:${orderTypeParam ?? ""}:${
          franchiseDeliveryTypeDisplayName ?? ""
        }:true`;

        // Check cache first (response cache)
        const cachedResponse = offersCache.get<UserOffersResult>(cacheKey);

        if (cachedResponse) {
          fetchUserOffers = cachedResponse;
        } else {
          // Use promise cache to prevent duplicate API calls
          // Custom caching logic to avoid caching empty offers
          const existingPromise = offersCache.get<Promise<UserOffersResult>>(
            `promise:${cacheKey}`
          );
          if (existingPromise) {
            fetchUserOffers = existingPromise;
          } else {
            const promiseFactory = (): Promise<OffersResult> => {
              // Make API call if not in cache
              // TODO: Add offline mode logic
              if (
                restaurantInfo?.config?.hasOfflineMode ||
                (selectedFranchise?.id && orderTypeParam)
              ) {
                const currentOrderType =
                  (orderTypeParam as OrderTypesEnum) ||
                  orderTypeParamState ||
                  OrderTypesEnum.TAKE_OUT;
                const loyaltyOrderType = mapToLoyaltyOrderType(
                  currentOrderType,
                  franchiseDeliveryTypeDisplayName
                );

                if (loyaltyOrderType) {
                  return getCustomerLoyaltyOffers({
                    // Sent only when the guest actually picked a branch.
                    // Offline mode has no ordering context and never does, so
                    // the proxy resolves the chain from restaurantId instead.
                    franchiseId: selectedFranchise?.id,
                    restaurantId,
                    orderType: loyaltyOrderType,
                    channel: getLoyaltyChannel(),
                    includesMilestoneOffers: true,
                    includesSplashOffers: true,
                    // Visitors only: the proxy accepts a signed-in
                    // caller's own short id and nothing else, and a stale one
                    // from a forwarded /r/<shortId> link would otherwise turn
                    // their offers into catalog offers with a 200.
                    customerShortId:
                      restaurantInfo?.config?.hasOfflineMode &&
                      userHelper.isVisitor()
                        ? customerShortId
                        : undefined,
                  })
                    .then((response) => ({
                      data: {
                        offers: [],
                        isFirstTimeOrder: false,
                        isTwistOfferAvailable: false,
                      },
                      loyaltyOffers: response.data,
                    }))
                    .catch(() => ({
                      data: {
                        offers: [],
                        isFirstTimeOrder: false,
                        isTwistOfferAvailable: false,
                      },
                    }));
                }

                // An order type the loyalty service cannot price gets no offers.
                return Promise.resolve(noOffers());
              } else {
                // Return empty offers for visitor when no franchise selected
                return Promise.resolve(noOffers());
              }
            };

            fetchUserOffers = promiseFactory().then((result) => {
              // Only cache non-empty offers
              if (result.data.offers.length > 0 && cacheKey) {
                offersCache.set(cacheKey, result, 15000);
              }

              return result;
            });

            // Cache the promise temporarily to prevent duplicate calls
            if (cacheKey) {
              offersCache.set(`promise:${cacheKey}`, fetchUserOffers, 1000); // Short-lived promise cache
            }
          }
        }
      }

      // Every run stores the offers, not only the one whose promise fetched
      // them: a run that reuses the cached promise after a resetCartAction
      // (which clears customerOffers) would otherwise leave the guest with no
      // rewards until something else changes.
      Promise.resolve(fetchUserOffers)
        .then((result) => {
          if (result?.loyaltyOffers) {
            dispatch(setCustomerLoyaltyOffers(result.loyaltyOffers));
          }
        })
        .catch(() => undefined);

      dispatch(setOrderDataLoading(true));
      dispatch(sendUserOfferRequest());
      const now = new Date();
      if (
        !alertSent &&
        userOfferRequests.count > 10 &&
        Math.abs(now.valueOf() - userOfferRequests.lastCalledAt) / (1000 * 60) <
          1
      ) {
        setAlertSent(true);
        const decodedToken = userHelper.parseJwt(token);
        logClientError(
          `${AlertErrorType.LinkOffers_Called_More_than_10_times}`,
          {
            customerId: decodedToken?.customer_id,
            restaurantInfo,
            selectedFranchise,
            order_type,
            isOrganizationCustomer,
          }
        );
      }

      const displayFranchiseMenu = restaurantInfo?.config?.displayFranchiseMenu;
      const mainFranchiseSlug = restaurantInfo?.mainFranchise?.slug;

      const fetchItemStatus = dispatch(
        fetchItemStatusRedux({
          slug:
            selectedFranchise?.slug ||
            ((displayFranchiseMenu && (mainFranchiseSlug as string)) as string),
          restaurantId,
        })
      );

      Promise.all([fetchUserOffers, fetchItemStatus])
        .then(([offersResponse]) => {
          if (offersResponse && offersResponse.data) {
            const { offers, isFirstTimeOrder, isTwistOfferAvailable } =
              offersResponse.data;
            const applicableOffers = offers.filter((offer: OfferType) => {
              const offerV2 = createOffer(offer);

              return !offerV2.containsDisabledItems(allDisabledItemsIds);
            });
            linkUserOffer(
              applicableOffers,
              isFirstTimeOrder,
              isTwistOfferAvailable,
              restaurantInfo?.timeZone
            );
          } else {
            console.error("Invalid response: ", offersResponse);
          }
        })
        .catch((error) => {
          console.error("Error in Promise.all: ", error);
        })
        .finally(() => {
          dispatch(setOrderDataLoading(false));
        });
    }
  }, [
    selectedFranchise?.id,
    token,
    order_type,
    isOrganizationCustomer,
    itemStatusRedux.disabledItemsIds.length,
    orderTypeParam,
    offlineOfferData?.isRedeemed,
    customerShortId,
    cartAndLoyalty.offersArray.length,
    bottomSheetModal.isOpen,
    franchiseDeliveryTypeDisplayName,
  ]);
};

export default useLinkOffers;
